/* eslint-disable no-console */
import jsYaml from 'js-yaml';
import { decode } from 'js-base64';
import { TreeNode } from '../type';
import { AppFrepLimit } from '@/pages/home/const';
import { toJS } from 'mobx';

// 拼接配置信息
export function formatDiffCode(origin: any) {
  const { newConfig, oldConfig } = origin;
  const { apiConfig: newApiConfig = '{}', routeConfig: newRouteConfig = {} } = newConfig;
  const { apiConfig: oldApiConfig = '{}', routeConfig: oldRouteConfig = {} } = oldConfig;
  const newRouteConfigStr = myStringify(newRouteConfig);
  const oldRouteConfigStr = myStringify(oldRouteConfig);
  const newCode = `# 路由配置
${newRouteConfigStr}
# Api配置
${jsYaml.safeLoad(newApiConfig)}`;

  const oldCode = `# 路由配置
${oldRouteConfigStr}
# Api配置
${jsYaml.safeLoad(oldApiConfig)}`;
  return {
    newCode,
    oldCode,
    lang: 'yaml',
    type: 'interface',
  };
}
// 插入灰度配置锚点
function myStringify(configObj = {}) {
  const str = JSON.stringify(configObj, null, '\t');
  return str.replace('"grayConfig"', `# 灰度配置
\t"grayConfig"`);
}

// 输出IDL格式数据
export function formatIDLCode(newConfig: string, oldConfig = '{}') {
  const newConfigJson = JSON.parse(newConfig);
  const oldConfigJson = JSON.parse(oldConfig);
  const { files: newFiles } = newConfigJson;
  const { files: oldFiles } = oldConfigJson;
  const newCode = formatIDLObj(newFiles);
  const oldCode = formatIDLObj(oldFiles);
  return {
    lang: 'go', newCode, oldCode, type: 'idl',
  };
}

// 格式化对象
export function formatIDLObj(IDLArr: any) {
  let str = '';
  if (!IDLArr) {
    return str;
  }

  Object.keys(IDLArr).forEach((key) => {
    str += `// ${key} \n ${decode(IDLArr[key])}`; // idl代码是 base64 加密的
  });
  return str;
}

// key - value 对象转化为数组
export function transformObjectToArray(origin: any) {
  if (!origin) {
    return [];
  }
  const data: any = origin || { '': { ...AppFrepLimit[1], limitLevel: 1 } };
  return Object.keys(data).map((key) => {
    if (data[key].limitLevel) {
      return { id: key, ...AppFrepLimit[data[key].limitLevel], limitLevel: data[key].limitLevel };
    }
    return { id: key, ...data[key], limitLevel: 0 };
  });
}
// 数组 转化为key - value
export function transformArrayToObject(origin: any) {
  if (origin && origin.length === 0) {
    return {};
  }
  let isFlag = true;
  const list = origin.reduce((data: any, item: any) => {
    if (!item.id || !item.qps || !item.qpm || !item.qpd) {
      isFlag = false;
    }
    // eslint-disable-next-line no-param-reassign
    data[item.id] = { ...item };
    return data;
  }, {});

  return isFlag ? list : isFlag;
}

// 判断 对象数组中相同key 的 value是否重名
export function getIsSameValue(data: TreeNode[], key: string, value?: string) {
  return data.filter((item: TreeNode) => item[key as keyof TreeNode] === value).length > 1;
}

// 判断是否有重复的Value
export function hasSameValue(data: TreeNode[]) {
  const origin = data.map((item: TreeNode) => item.name);
  const newData = Array.from(new Set(origin));
  return origin.length === newData.length;
}

export function hasSameValueLoop(data: TreeNode[]) {
  let isFlag = true;
  function loop(list: TreeNode[]) {
    list.forEach((item: TreeNode) => {
      if (item.children && item.children.length) {
        loop(item.children);
      }
    });
    if (!hasSameValue(list)) {
      isFlag = false;
    }
  }
  loop(data);

  return isFlag;
}

// 判断是否有空的 name
export function hasBlankName(data: TreeNode[], key = 'name') {
  let isFlag = true;
  function loop(list: TreeNode[]) {
    list.forEach((item: TreeNode) => {
      if (item.children && item.children.length) {
        loop(item.children);
      }
      if (!item[key as keyof TreeNode] && key === 'name') {
        isFlag = false;
      }
      if (!item[key as keyof TreeNode] && (!item.children || !item.children.length) && key !== 'name') {
        isFlag = false;
      }
    });
  }
  loop(data);

  return isFlag;
}

// 重新构建rpc参数的数据结构
export function createRpcList(data: TreeNode[], method = 'GET') {
  const source: TreeNode[] = [];
  const sourceMap: any = { };
  function loop(list: TreeNode[], context: TreeNode[], parentKey = '') {
    list.forEach((item: any) => {
      const id = `${parentKey ? `${parentKey}-` : ''}${item.name || '[]'}`;
      const obj = {
        id,
        ...item,
        source: getSource(item.source, method),
        children: [],
      };
      sourceMap[id] = obj.children;
      if (item.children && item.children.length) {
        loop(item.children, obj.children, id);
      }
      context.push(obj);
    });
  }
  loop(data, source);

  sourceMap['-1'] = source.map((item: any) => {
    if (isComplexType(item.type)) {
      return {
        ...item,
        source: getSource(item.source, method),
        isExpand: true,
        children: [{
          ...item.children[0],
          children: [],
        }],
      };
    }
    return { ...item, source: getSource(item.source, method), children: [] };
  });

  return sourceMap;
}

// 重新构造 接口的配置信息
export function buildApiConfig(data: TreeNode[]) {
  const source: TreeNode[] = [];
  let ids = 0;
  function loop(list: TreeNode[], context: TreeNode[], parentId = '', dataPath = '') {
    list.forEach((item: TreeNode, key: number) => {
      ids++;
      const id = `${parentId ? `${parentId}-` : ''}${key}`;
      const subPath = item.name ? `.${item.name}` : `[${key}]`;
      const obj = {
        // @ts-ignore
        id,
        ...item,
        dataPath: dataPath ? (dataPath + subPath) : item.name,
        children: [],
        isExpand: id.split('-').length < 2,
      };
      if (item.children && item.children.length) {
        loop(item.children, obj.children, id, obj.dataPath);
      }
      context.push(obj);
    });
  }
  loop(data, source);
  return { source, ids };
}
// 构造 rpc 接口数据信息
export function buildApiRpcConfig(data: TreeNode[]) {
  const source: TreeNode[] = [];
  let ids = 0;
  function loop(list: TreeNode[], context: TreeNode[], parentId = '') {
    list.forEach((item: any) => {
      ids++;
      const id = `${parentId ? `${parentId}-` : ''}${item.name || '[]'}`;
      const obj: TreeNode = {
        id,
        ...item,
        source: item.source,
        children: [],
        isExpand: id.split('-').length < 1,
      };
      if (item.children && item.children.length) {
        loop(item.children, obj.children, id);
      }
      context.push(obj);
    });
  }
  loop(data, source);
  return { source, ids };
}
//  GET 在query里面取参数  POST 在body里面取参数
export function getSource(source: string | number, method: string) {
  const regstring = method === 'GET' ? '.body.' : '.query.';
  const reg = method === 'GET' ? '.query.' : '.body.';

  return (String(source) || '').replace(regstring, reg);
}

// 修改数的结构
export function modifyApiConfig(data: TreeNode[]) {
  const source: TreeNode[] = [];
  function loop(list: TreeNode[], context: TreeNode[]) {
    list.forEach((item: any) => {
      const obj = {
        ...item,
        children: [],
      };
      if (isComplexType(item.type) && item.subType === 'object') {
        if (item.children[0].children && item.children[0].children.length) {
          loop(item.children[0].children, obj.children);
        }
      }
      if (item.type === 'object') {
        loop(item.children, obj.children);
      }
      context.push(obj);
    });
  }
  loop(data, source);
  return source;
}

// 是否显示展示按钮
export function isCanFolder(data: TreeNode) {
  if (isComplexType(data.type) && data.children.length > 0) {
    return true;
  }
  if (data.type === 'object' && data.children.length > 0) {
    return true;
  }
  return false;
}

export function isSimpleType(type: string) {
  return ['number', 'string', 'boolean'].includes(type);
}

export function isComplexType(type: string) {
  return ['array', 'dict'].includes(type);
}

// 获取所有节点的个数
export function getAllChildNum(list: TreeNode[]) {
  let number = 0;
  function loop(data: TreeNode[]) {
    data.forEach((item: TreeNode) => {
      number++;
      if (isComplexType(item.type)
        && item.children
        && item.children[0]
        && item.children[0].children
        && item.children[0].children.length) {
        loop(item.children[0].children);
      }
      if (item.type === 'object') {
        loop(item.children);
      }
    });
  }
  loop(list);
  return number;
}

// 求数组的并集
export function getDiffChild(list: TreeNode[], config: TreeNode[]) {
  return (config || []).filter((item: TreeNode) => list.findIndex((data: any) => data.id === item.id) === -1);
}

export interface SuperTreeNode { // 继承接口Shape
  title: string;
  value: string;
  type: string;
  disabled: boolean;
  subType: string | undefined;
  children: SuperTreeNode[];
}
// 重新构建父子节点的关系   isFilter 用来过滤
export function buildParentChildren(list: TreeNode[], type = '', isFilter = false) {
  const result: SuperTreeNode[] = [];

  function loop(data: TreeNode[], context: SuperTreeNode[], pStr: string) {
    data.forEach((item) => {
      if (['dict', 'array'].includes(item.type) && item.subType === 'object') {
        // eslint-disable-next-line no-param-reassign
        item.children = item.children[0].children;
      }
      if (item.name) {
        const name = `${item.name}${item.type === 'array' ? '[]' : ''}`; // 这里加上类型, array 加上 []
        const obj: SuperTreeNode = {
          title: item.name,
          value: `${pStr}.${name}`,
          type: item.type,
          subType: item.subType,
          disabled: isFilter ? !['object', 'dict'].includes(item.type) : isFilter,
          children: [],
        };
        if (item.children && item.children.length) {
          const str = `${pStr}.${name}`;
          loop(item.children, obj.children, str);
        }
        context.push(obj);
      }
    });
  }

  loop(toJS(list), result, type);
  return result;
}
